import { useMemo } from 'react';
import { cn } from '@/lib/cn';
import { parseMarkdown, type MdBlock, type MdSpan } from '@/lib/markdown';
import { getBridge } from '@/platform';

/**
 * Ответ ассистента с разметкой.
 *
 * Агент отвечает Markdown, и без разбора человек читает служебные символы.
 * Разметка превращается в React-элементы, а не в HTML: содержимое приходит от
 * модели, и вставлять его в страницу как код нельзя.
 */
export function Markdown({ text, className }: { text?: string | null; className?: string }) {
  // Сообщение может прийти без текста — например, с одним вложением
  const blocks = useMemo(() => parseMarkdown(text ?? ''), [text]);

  return (
    <div className={cn('grid min-w-0 gap-2 [overflow-wrap:anywhere]', className)}>
      {blocks.map((block, index) => (
        <Block key={index} block={block} />
      ))}
    </div>
  );
}

function Block({ block }: { block: MdBlock }) {
  if (block.kind === 'rule') return <hr className="border-line/70" />;

  if (block.kind === 'code') {
    return (
      <pre className="overflow-x-auto rounded-control border border-line/70 bg-surface-2 p-2.5 text-[11.5px] leading-[1.45]">
        <code>{block.text}</code>
      </pre>
    );
  }

  if (block.kind === 'heading') {
    // Заголовки внутри пузыря не должны спорить с заголовком экрана:
    // выделяем начертанием, а не размером
    return (
      <strong className={cn('block font-medium', block.level <= 2 && 'text-[13.5px]')}>
        <Spans spans={block.spans} />
      </strong>
    );
  }

  if (block.kind === 'quote') {
    return (
      <blockquote className="border-l-2 border-brand/60 pl-2.5 opacity-90">
        <Spans spans={block.spans} />
      </blockquote>
    );
  }

  if (block.kind === 'table') {
    // Таблица шире экрана прокручивается внутри себя: иначе горизонтальную
    // прокрутку получает вся страница
    return (
      <div className="-mx-0.5 overflow-x-auto">
        <table className="w-full border-collapse text-[11.5px] leading-[1.45]">
          {block.head.length > 0 ? (
            <thead>
              <tr>
                {block.head.map((cell, index) => (
                  <th
                    key={index}
                    className="border border-line/70 bg-surface-2 px-1.5 py-1 text-left font-medium"
                  >
                    <Spans spans={cell} />
                  </th>
                ))}
              </tr>
            </thead>
          ) : null}
          <tbody>
            {block.rows.map((row, rowIndex) => (
              <tr key={rowIndex}>
                {row.map((cell, cellIndex) => (
                  <td key={cellIndex} className="border border-line/70 px-1.5 py-1 align-top">
                    <Spans spans={cell} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  if (block.kind === 'list') {
    return (
      <ul className="grid gap-1">
        {block.items.map((item, index) => (
          <li key={index} className="flex gap-2">
            <span className={cn('shrink-0', block.ordered ? 'tabular-nums' : 'opacity-70')} aria-hidden>
              {item.marker}
            </span>
            <span>
              <Spans spans={item.spans} />
            </span>
          </li>
        ))}
      </ul>
    );
  }

  return (
    <p>
      <Spans spans={block.spans} />
    </p>
  );
}

function Spans({ spans }: { spans: MdSpan[] }) {
  return (
    <>
      {spans.map((span, index) => {
        if (span.kind === 'break') return <br key={index} />;
        if (span.kind === 'strong') return <strong key={index} className="font-medium">{span.text}</strong>;
        if (span.kind === 'em') return <em key={index}>{span.text}</em>;
        if (span.kind === 'code') {
          return (
            <code key={index} className="rounded bg-surface-2 px-1 py-0.5 text-[11.5px]">
              {span.text}
            </code>
          );
        }
        if (span.kind === 'link') {
          return (
            <button
              key={index}
              type="button"
              // Ссылка открывается мостом: переход в самом WebView выгрузил бы
              // приложение вместе с открытым диалогом
              onClick={() => getBridge().openLink(span.href)}
              className="bd-focus text-left underline underline-offset-2 [overflow-wrap:anywhere]"
            >
              {span.text}
            </button>
          );
        }
        return <span key={index}>{span.text}</span>;
      })}
    </>
  );
}
